import classNames from "classnames";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useLocale } from "../../contexts/LocaleContext";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import { HomeSection, goToHomeSection } from "../../utils/homeSectionNavigation";
import styles from "./SiteNav.module.scss";

type NavState = "default" | "scrolled" | "solid";

type SiteNavProps = {
  solidThreshold?: number;
};

const SCROLLED_THRESHOLD = 50;

export default function SiteNav({ solidThreshold = 360 }: SiteNavProps) {
  const nav = useNavigate();
  const location = useLocation();
  const { locale, setLocale, messages } = useLocale();
  const windowSize = useWindowDimensions();
  const isMobile = windowSize.width < 500;
  const [navState, setNavState] = useState<NavState>("default");

  useEffect(() => {
    let frameId = 0;

    const updateNavState = () => {
      const scrollY = window.scrollY;
      const nextState: NavState = scrollY > solidThreshold ? "solid" : scrollY > SCROLLED_THRESHOLD ? "scrolled" : "default";

      setNavState((prev) => (prev === nextState ? prev : nextState));
      frameId = 0;
    };

    const handleScroll = () => {
      if (frameId !== 0) return;

      frameId = window.requestAnimationFrame(updateNavState);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    updateNavState();

    return () => {
      window.removeEventListener("scroll", handleScroll);

      if (frameId !== 0) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [solidThreshold]);

  const handleScrollNavigation = useCallback(
    (section: HomeSection) => {
      goToHomeSection(nav, location.pathname, section, isMobile);
    },
    [isMobile, location.pathname, nav]
  );

  const handleLogoClick = useCallback(() => {
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      return;
    }

    nav("/");
  }, [location.pathname, nav]);

  return (
    <nav
      className={classNames(
        navState === "solid" ? styles.navbarDefault : navState === "scrolled" ? styles.navbarScrolled : styles.navbar
      )}
    >
      <div className={styles.navContent}>
        <button type="button" className={styles.logo} onClick={handleLogoClick} aria-label={messages.nav.homeAriaLabel}>
          <div className={styles.logoWhite} />
          <div className={styles.logoColor} />
        </button>

        <div className={styles.tabs}>
          <button type="button" hidden={isMobile} className={styles.navLink} onClick={() => nav("/about")}>
            {messages.nav.about}
          </button>
          <button type="button" hidden={isMobile} className={styles.navLink} onClick={() => handleScrollNavigation("services")}>
            {messages.nav.services}
          </button>
          <button type="button" hidden={isMobile} className={styles.navLink} onClick={() => handleScrollNavigation("contacts")}>
            {messages.nav.contacts}
          </button>
          <button type="button" hidden={isMobile} className={styles.navLink} onClick={() => nav("/career")}>
            {messages.nav.career}
          </button>

          <div className={styles.actions}>
            <div className={styles.localeSwitcher} role="group" aria-label={messages.nav.switcherAriaLabel}>
              <button
                type="button"
                className={classNames(styles.localeButton, locale === "ru" && styles.localeButtonActive)}
                onClick={() => setLocale("ru")}
                aria-pressed={locale === "ru"}
              >
                RU
              </button>
              <button
                type="button"
                className={classNames(styles.localeButton, locale === "en" && styles.localeButtonActive)}
                onClick={() => setLocale("en")}
                aria-pressed={locale === "en"}
              >
                EN
              </button>
            </div>

            <button type="button" className={styles.headerButton} onClick={() => handleScrollNavigation("contacts")}>
              {messages.nav.contactCta}
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}
